import React from 'react';
import {
  Card, Collapse, Divider, Icon, Modal, Spin, Tag
} from 'antd';
import moment from 'moment';
import settings from 'settings';
import { durationString, parseDuration } from 'library/time';
import { getCarrier } from 'selectors/travel.selectors';
import { getHexTextColorHex } from 'library/colors';
import _ from 'lodash';
import {
  actionDot, notesDot, notificationDot, NOTIFICATION_STATUS_WORDS, notificationStatus, signOnTriangle, validationIcon
} from 'components/Timeline/indicators';
import { formatAvailableSignOn, isOta, toLocalValue } from './availableSignOn';

const DEFAULT_TABLE_FILTER_ACTIVE_COLOR = settings.APP_CONFIG.defaultTableFilterActiveColor;
const DEFAULT_TABLE_FILTER_INACTIVE_COLOR = settings.APP_CONFIG.defaultTableFilterInactiveColor;

// form utils
export const getKey = item => item.id;
export const getLabel = item => item.name;
export const getActivityLabel = item => `${item.name} ${item.activity_type.code ? `(${item.activity_type.code})` : ''}`;
export const getNameLabel = item => `${item.last_name}, ${item.first_name}`;
export const convertFromSelect = (values) => {
  const key = Object.keys(values)[0];
  if (values[key] && values[key].key) {
    const data = {};
    data[key] = {
      id: values[key].key,
      name: values[key].label
    };

    return data;
  }

  return values;
};
export const convertToSelect = (value, keys = ['name']) => {
  if (value && value.id) {
    return {
      key: value.id,
      label: keys.map(key => value[key])
        .join(', ')
    };
  }
  return value;
};

export const getSelect = (formValue, defaultValue, key) => (formValue ? convertToSelect(formValue, key) : convertToSelect(defaultValue, key));
export const getSelects = (value, key = ['name'], defaultValue = []) => (value ? value.map(item => convertToSelect(item, key)) : defaultValue.map(item => convertToSelect(item, key)));

export const getStatusName = (statusChar) => {
  switch (statusChar) {
  case 'P':
    return 'Pending';
  case 'R':
    return 'Running';
  case 'S':
    return 'Success';
  case 'F':
    return 'Failed';
  default:
    return '';
  }
};

export const getStatusColour = (statusChar) => {
  switch (statusChar) {
  case 'P':
    return 'orange';
  case 'R':
    return 'blue';
  case 'S':
    return 'green';
  case 'F':
    return 'red';
  default:
    return '';
  }
};

export const groupItemContent = (group, spinning = false) => (
  <Spin spinning={spinning} indicator={<Icon type="loading" spin />}>
    <div>
      {group.email && (
        <div>
          <strong>Email:</strong>
          {' '}
          {group.email}
        </div>
      )}
      {group.first_name && (
        <div>
          <strong>First Name:</strong>
          {' '}
          {group.first_name}
        </div>
      )}
      {group.last_name && (
        <div>
          <strong>Last Name:</strong>
          {' '}
          {group.last_name}
        </div>
      )}
      {group.tags && group.tags.length > 0 && (
        <Divider style={{
          width: '100px',
          fontSize: '12px'
        }}
        >
          <Icon type="tags" theme="filled" />
          Tags
        </Divider>
      )}
      {group.tags && group.tags.map(tag => (
        <div key={tag.tag_id}>
          <div style={{ fontSize: '10px' }}><Tag style={{ fontSize: '10px' }}>{tag.tag.qualified_name}</Tag></div>
        </div>
      ))}
    </div>
  </Spin>
);

/**
 * @description Default activity renderer
 */
export const activityItemContent = (item, timeFormat = 'YYYY-MM-DD HH:mm:ss', options = {}) => {
  let { duration } = item.activity;

  if (item.start && item.end) {
    const difference = moment.duration(item.end.diff(item.start));
    const days = difference.days() ? `${Math.trunc(difference.asDays())}d` : '';
    const hours = `${difference.hours()}hrs`;
    const mins = `${difference.minutes()}min`;
    duration = (`${days} ${hours} ${mins}`).trim();
  }
  let userName = 'UNALLOCATED';
  if (item.user && item.user.first_name) {
    userName = `${item.user.last_name}, ${item.user.first_name}`;
  }
  return (
    <div>
      {item.user && (
        <div>
          <strong>User:</strong>
          {' '}
          {userName}
        </div>
      )}
      <div>
        <strong>Activity:</strong>
        {' '}
        {item.activity.name}
      </div>
      {item.start && (
        <div>
          <strong>Start:</strong>
          {' '}
          {item.start.format(timeFormat)}
        </div>
      )}
      {item.end && (
        <div>
          <strong>End:</strong>
          {' '}
          {item.end.format(timeFormat)}
        </div>
      )}
      {options.showSignOn && isOta(item.activity) && (
        <div>
          {signOnTriangle({ set: !!item.available_sign_on, inline: true })}
          <strong>Available sign-on:</strong>
          {' '}
          {formatAvailableSignOn(toLocalValue(item.available_sign_on || null, item.start))}
        </div>
      )}
      <Tag color={item.color} style={{ color: getHexTextColorHex(item.color) }}>
        <strong>Duration:</strong>
        <span>{duration}</span>
      </Tag>
      {item.default_notifications && item.default_notifications.length > 0 && (
        <Divider style={{
          width: '100px',
          fontSize: '12px'
        }}
        >
          <Icon type="notification" theme="filled" />
          {' '}
          Default Notifications
        </Divider>
      )}
      {item.default_notifications && item.default_notifications.map(notification => (
        <div key={notification.id}>
          <div style={{ fontSize: '10px' }}>
            <strong>{notification.type.name}</strong>
            {' '}
            {notification.status && (
              <Tag
                color={getStatusColour(notification.status)}
                style={{
                  float: 'right',
                  fontSize: '8px'
                }}
              >
                {getStatusName(notification.status)}
              </Tag>
            )}
          </div>
          <div style={{ fontSize: '10px' }}>
            {durationString(notification.delta)}
            {' '}
            {notification.is_before ? 'Before' : 'After'}
          </div>
        </div>
      ))}
      {item.tags && item.tags.length > 0 && (
        <Divider style={{
          width: '100px',
          fontSize: '12px'
        }}
        >
          <Icon type="tags" theme="filled" />
          Tags
        </Divider>
      )}
      {item.tags && item.tags.map(tag => (
        <div key={tag.tag_id}>
          <div style={{ fontSize: '10px' }}><Tag style={{ fontSize: '10px' }}>{tag.tag.qualified_name}</Tag></div>
        </div>
      ))}
      {item.notifications && item.notifications.length > 0 && (
        <Divider style={{
          width: '100px',
          fontSize: '12px'
        }}
        >
          {notificationDot({ notifications: item.notifications, inline: true })}
          <Icon type="notification" theme="filled" />
          {' '}
          {`Notifications — ${NOTIFICATION_STATUS_WORDS[notificationStatus(item.notifications)]}`}
        </Divider>
      )}
      {item.notifications && item.notifications.map(notification => (
        <div key={notification.id}>
          <div style={{ fontSize: '10px' }}>
            <strong>{notification.type.name}</strong>
            {' '}
            {notification.status && (
              <Tag
                color={getStatusColour(notification.status)}
                style={{
                  float: 'right',
                  fontSize: '8px'
                }}
              >
                {getStatusName(notification.status)}
              </Tag>
            )}
          </div>
          <div style={{ fontSize: '10px' }}>
            {durationString(notification.delta)}
            {' '}
            {notification.is_before ? 'Before' : 'After'}
          </div>
        </div>
      ))}
      {item.notes && (
        <Divider style={{
          width: '100px',
          fontSize: '12px'
        }}
        >
          {notesDot({ inline: true })}
          <Icon type="file-text" theme="filled" />
          {' '}
          Notes
        </Divider>
      )}
      {item.notes && (<div>{item.notes}</div>)}
      {item.admin_notes && (
        <>
          <Divider style={{
            width: '100px',
            fontSize: '12px'
          }}
          >
            {notesDot({ admin: true, inline: true })}
            <Icon type="file-text" theme="filled" />
            {' '}
            Admin Notes
          </Divider>
          <div>{item.admin_notes}</div>
        </>
      )}
      {item.validations && item.validations.length > 0 && (
        <Divider style={{
          width: '100px',
          fontSize: '12px'
        }}
        >
          {validationIcon({ inline: true })}
          <Icon type="medicine-box" theme="filled" />
          {' '}
          Validation Errors
        </Divider>
      )}
      {item.validations && item.validations.length > 0 && (
        <Collapse accordion>
          {item.validations.map(validation => (
            <Collapse.Panel
              showArrow={false}
              key={validation.key}
              style={{ fontSize: '10px' }}
              header={`${validation.status}: ${validation.name} / ${validation.triggered_on}`}
              extra={<Icon type="more" />}
            >
              <div style={{ fontSize: '10px' }}>
                <div>{validation.error_msg}</div>
              </div>
            </Collapse.Panel>
          ))}
        </Collapse>
      )}
      {item.latest_action && (
        <>
          <Divider style={{ width: '100px', fontSize: '12px' }}>
            {actionDot({ resolved: !item.has_unresolved_actions, inline: true })}
            <Icon type="thunderbolt" theme="filled" />
            {' '}
            {`Latest SMS action — ${item.has_unresolved_actions ? 'unresolved' : 'resolved'}`}
          </Divider>
          <div style={{ fontSize: '10px' }}>
            <div><strong>Type:</strong> {item.latest_action.action_type_name}</div>
            <div><strong>Notification:</strong> {item.latest_action.notification_type_names}</div>
            <div><strong>Description:</strong> {item.latest_action.description}</div>
            {item.latest_action.sms_sent_timestamp && (
              <div><strong>Time sent:</strong> {moment(item.latest_action.sms_sent_timestamp).format(timeFormat)}</div>
            )}
            {!item.latest_action.sms_sent_timestamp && item.latest_action.sms_send_pending && (
              <div><strong>Time sent:</strong> Sending...</div>
            )}
            {!item.latest_action.sms_sent_timestamp && !item.latest_action.sms_send_pending && item.latest_action.sms_send_failed && (
              <div><strong>Time sent:</strong> Failed to send</div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

/**
 * @description Renderer for travel bookings
 */
export const travelItemContent = (item, timeFormat) => {
  const { booking } = item;
  return (
    <div key={item.id}>
      <div>
        <strong>Traveller:</strong>
        {' '}
        {`${item.user.last_name.toUpperCase()}, ${item.user.first_name}`}
      </div>
      <div>
        <strong>Travel Type:</strong>
        {' '}
        {`Travel ${item.booking.travel_type}`}
      </div>
      {booking.notes && (
        <Divider style={{
          width: '100px',
          fontSize: '12px'
        }}
        >
          <Icon type="file-text" theme="filled" />
          {' '}
          Notes
        </Divider>
      )}
      {booking.notes && (<div>{booking.notes}</div>)}
      <Divider style={{
        width: '100px',
        fontSize: '12px'
      }}
      >
        <Icon type="apartment" />
        {' '}
        Legs
      </Divider>
      {
        booking.legs.map((leg) => {
          const site = (leg.route.departure_location.code === 'PER' && ['PHE', 'BYP', 'ZNE'].includes(leg.route.arrival_location.code))
            || (leg.route.arrival_location.code === 'PER' && ['PHE', 'BYP', 'ZNE'].includes(leg.route.departure_location.code));
          return ([
            <div key={leg.id} style={{ margin: '0 0 10px 0' }}>
              <div><strong>{leg.reference_number.toUpperCase()}</strong></div>
              <div>
                <strong>Route:</strong>
                {' '}
                {leg.route.name}
              </div>
              <div>
                <strong>Depart:</strong>
                {' '}
                {moment.utc(leg.departure_date)
                  .format(timeFormat)}
                {' '}
                [
                {leg.route.departure_location.code}
                ]
              </div>
              <div>
                <strong>Arrive:</strong>
                {' '}
                {moment.utc(leg.arrival_date)
                  .format(timeFormat)}
                {' '}
                [
                {leg.route.arrival_location.code}
                ]
              </div>
              <div><Tag>{site ? 'Site' : 'FAP'}</Tag></div>
            </div>
          ]);
        })
      }
    </div>
  );
};

/**
 * @description Renderer for leave items
 */
export const leaveItemContent = (item, timeFormat) => {
  const { leave } = item;
  return (
    <div>
      <div>
        <strong>Type:</strong>
        {' '}
        {leave.type.name}
      </div>
      <div>
        <strong>Status:</strong>
        {' '}
        {leave.status.status_on_timeline}
      </div>
      <div>
        <strong>Starts:</strong>
        {' '}
        {leave.start_time.format(timeFormat)}
      </div>
      <div>
        <strong>Ends:</strong>
        {' '}
        {leave.end_time.format(timeFormat)}
      </div>
    </div>
  );
};

export const activityCard = data => (
  <div className="bhp-card">
    <div><strong>{data.model.toUpperCase()}</strong></div>
    <Tag
      color={data.color}
      style={{
        color: getHexTextColorHex(data.color),
        cursor: 'default'
      }}
    >
      {data.name}
    </Tag>
    <div>
      <strong>Duration:</strong>
      {' '}
      {durationString(data.duration)}
    </div>
  </div>
);
export const assignmentCard = data => (
  <div className="bhp-card">
    <div><strong>{data.model.toUpperCase()}</strong></div>
    <div>
      <strong>Roster:</strong>
      {' '}
      {data.version ? `${data.version.parent_roster.name}/${data.version.name}` : 'NA'}
    </div>
    {data.user && (
      <div>
        <strong>User:</strong>
        {' '}
        {data.user.last_name}
        ,
        {' '}
        {data.user.first_name}
      </div>
    )}
    {!data.user && (
      <div>
        <strong>User:</strong>
        {' '}
        <strong>UNALLOCATED</strong>
      </div>
    )}
    <div>
      <strong>Activity:</strong>
      {' '}
      <Tag
        color={data.activity.color}
        style={{
          color: getHexTextColorHex(data.activity.color),
          cursor: 'default'
        }}
      >
        {data.activity.name}
      </Tag>
    </div>
    <div>
      <strong>Start:</strong>
      {' '}
      {moment(data.start)
        .format('Do MMM HH:mm')}
    </div>
    <div>
      <strong>End:</strong>
      {' '}
      {moment(data.end)
        .format('Do MMM HH:mm')}
    </div>
    {data.notes && (
      <div>
        <strong>Notes:</strong>
        {data.notes}
      </div>
    )}
    <div>
      <strong>Duration:</strong>
      {' '}
      {durationString(data.activity.duration)}
    </div>
  </div>
);
export const userleaveCard = data => (
  <div className="bhp-card">
    <div><strong>{data.name.toUpperCase()}</strong></div>
    <div>
      <strong>User:</strong>
      {' '}
      {data.user && `${data.user.last_name}, ${data.user.first_name}`}
    </div>
    <div>
      <strong>Type:</strong>
      {' '}
      {data.type.name}
    </div>
    <div>
      <strong>Status:</strong>
      {' '}
      {data.status.name}
    </div>
    <div>
      <strong>Start:</strong>
      {' '}
      {moment(data.start_datetime)
        .format('Do MMM')}
    </div>
    <div>
      <strong>End:</strong>
      {' '}
      {moment(data.end_datetime)
        .format('Do MMM')}
    </div>
  </div>
);
export const userCard = data => (
  <div className="bhp-card">
    <div><strong>{data.model.toUpperCase()}</strong></div>
    <div>
      <strong>Name:</strong>
      {' '}
      {getNameLabel(data)}
    </div>
    <div>
      <strong>Email:</strong>
      {' '}
      {data.email}
    </div>
  </div>
);
export const bookingCard = data => (
  <div className="bhp-card">
    <div><strong>{data.model.toUpperCase()}</strong></div>
    <div>
      <strong>User:</strong>
      {' '}
      {data.user && `${data.user.last_name}, ${data.user.first_name}`}
    </div>
    <div>
      <strong>Carrier:</strong>
      {' '}
      {data.leg ? getCarrier(data.leg) : 'NA'}
    </div>
  </div>
);
export const displayCard = data => (
  <Card>
    {Object.keys(data)
      .map((item) => {
        let text;
        let label;
        switch (item) {
        case 'id':
          text = '';
          break;
        case 'active':
          label = item;
          text = data[item] ? <Icon type="check-circle" /> : <Icon type="close-circle" />;
          break;
        case 'color':
          label = 'Colour';
          text = <Tag color={data.color} style={{ color: getHexTextColorHex(data.color) }}>{data.color}</Tag>;
          break;
        case 'duration':
          label = item;
          text = durationString(data.duration);
          break;
        case 'model':
          label = 'theme';
          text = data.model.toUpperCase();
          break;
        default:
          label = item;
          text = data[item];
          break;
        }
        return (
          text && (
            <div key={item}>
              <strong>
                {label.toUpperCase()}
                :
              </strong>
              {' '}
              {text}
            </div>
          )
        );
      })}
  </Card>
);

export const durationValidator = (rule, value, callback) => {
  const duration = parseDuration(value);
  if (duration.days === 0 && duration.hours === 0 && duration.minutes < 15) {
    callback('Duration must not be less than 15 minutes');
  }
  callback();
};
export const setUpperDateRange = (value, endValue) => {
  if (!value || !endValue) {
    return false;
  }
  return value.valueOf() > endValue.valueOf();
};

export const setLowerDateRange = (value, startValue) => {
  if (!startValue || !value) {
    return false;
  }
  return value.valueOf() < startValue.valueOf();
};

// = =======================================
// BROWSER CHECK
// = =======================================

export const getChromeVersion = () => {
  // https://stackoverflow.com/questions/4900436/how-to-detect-the-installed-chrome-version
  let version = -1;
  if (/chrom(e|ium)/.test(navigator.userAgent.toLowerCase())) {
    const raw = navigator.userAgent.match(/Chrom(e|ium)\/([0-9]+)\./);
    if (raw) {
      version = parseInt(raw[2], 10);
    }
  }
  return version;
};

export const getIEVersion = () => {
  // https://blogs.msdn.microsoft.com/giorgio/2009/04/14/how-to-detect-ie8-using-javascript-client-side/
  let rv = -1;
  if (navigator.appName === 'Microsoft Internet Explorer') {
    const ua = navigator.userAgent;
    const re = new RegExp('MSIE ([0-9]{1,}[.0-9]{0,})');
    if (re.exec(ua) != null) rv = parseFloat(RegExp.$1);
  }
  return rv;
};

// = =======================================
// FILTER AND SORT HELPERS
// = =======================================

export const getSortField = (sorter = {}) => {
  const sortField = [];
  if (!_.isEmpty(sorter)) {
    sortField.push({
      id: sorter.field,
      desc: sorter.order === 'descend'
    });
  }
  return sortField;
};

export const getFieldFilters = (filters = {}) => {
  const filterList = [];
  const filterKeys = Object.keys(filters);
  for (let i = 0; i < filterKeys.length; i++) {
    const key = filterKeys[i];
    if (filters[key].length > 0) {
      const value = filters[key].join(',');
      filterList.push({
        id: key,
        value
      });
    }
  }
  return filterList;
};

export const getCustomFilters = (filters = {}) => {
  const filterList = [];

  const filterKeys = Object.keys(filters);
  for (let i = 0; i < filterKeys.length; i++) {
    const key = filterKeys[i];
    const filter = filters[key] || {};
    if (filter.searchFields && filter.searchFields.length) {
      const ignoreFilterIfEmptyValue = filter.ignoreFilterIfEmptyValue == null || filter.ignoreFilterIfEmptyValue === undefined ? true : filter.ignoreFilterIfEmptyValue;
      const valueIsEmpty = !filter.searchFields || !filter.searchFields.length;

       if (!(ignoreFilterIfEmptyValue && valueIsEmpty)) {
        filter.searchFields.forEach((field) => {
          const fieldName = filter.contains ? `${field}__icontains` : field;
          const prependValue = filter.prependValue || '';
          const appendValue = filter.appendValue || '';
          const searchValue = _.isBoolean(filter.searchValue) ? filter.searchValue : (filter.searchValue || '');
          const value = prependValue + searchValue + appendValue;
          filterList.push({
            id: fieldName,
            value
          });
        });
      }
    }
  }
  return filterList;
};

export const getFilterIcon = (isActive = false) => (
  <Icon type="filter" style={{ color: isActive ? DEFAULT_TABLE_FILTER_ACTIVE_COLOR : DEFAULT_TABLE_FILTER_INACTIVE_COLOR }} />
);

export const getActiveIcon = (isActive = false) => (
  isActive ? <Icon type="check-circle" className="bhp-active" /> : <Icon type="close-circle" className="bhp-inactive" />
);

export const showFilterDropdown = (filters = {}, key = '', visible = false) => ({
  ...filters,
  [key]: {
    ...filters[key],
    filterDropdownVisible: visible
  }
});

export const getTextFilterInputChange = (filters = {}, key = '', value = '') => ({
  ...filters,
  [key]: {
    ...filters[key],
    searchValue: value
  }
});

export const getTextFilterSearchValue = (filters = {}, key = '') => ({
  ...filters,
  [key]: {
    ...filters[key],
    filterDropdownVisible: false,
    filtered: !!filters[key].searchValue
  }
});

export const clearTextFilterSearchValue = (filters = {}, key = '') => ({
  ...filters,
  [key]: {
    ...filters[key],
    filterDropdownVisible: false,
    filtered: false,
    searchValue: ''
  }
});

export const getTableState = (page, pageSize, filters, sorter) => ({
  page,
  pageSize,
  filters,
  sorter
});

// = =======================================
// PROMPTS
// = =======================================

export const promptSaveChanges = (okCallback, cancelCallback) => {
  Modal.confirm({
    title: 'Save Changes?',
    content: (
      <div>
        <p>
          You have made changes to this form which has not been saved.
        </p>
        <p>
          Do you wish to save these changes?
        </p>
      </div>
    ),
    okText: 'Yes - Save Changes',
    cancelText: 'No - Discard Changes',
    onOk() {
      okCallback();
    },
    onCancel() {
      cancelCallback();
    }
  });
};

export const promptNavigation = (okCallback, cancelCallback) => {
  Modal.confirm({
    title: 'Unsaved Changes',
    content: (
      <div>
        <p>
          You have made changes to the timeline that have not been saved.
        </p>
        <p>
          Do you wish to discard these changes?
        </p>
      </div>
    ),
    icon: 'exclamation-circle',
    okText: 'No - Remain here',
    cancelText: 'Yes - Discard Changes',
    onOk() {
      okCallback();
    },
    onCancel() {
      cancelCallback();
    }
  });
};

// = =======================================
// NAVIGATION
// = =======================================

export const blockNavigation = (isBlocked = false) => {
  window.onbeforeunload = (isBlocked ? () => true : null);
};

export const isNavigationBlocked = () => window.onbeforeunload !== undefined && window.onbeforeunload !== null;

// = =======================================
// PATH HELPERS
// = =======================================
export const pathJoin = (pathList = []) => {
  let joinedPath = pathList
    .map((path) => {
      // -- Remove beginning and trailing "/" from each path
      // -- and join all paths together with a "/".
      // ---
      if (path[0] === '/') {
        return path.slice(1);
      }
      if (path[path.length - 1] === '/') {
        return path.slice(0, path.length - 1);
      }

      return path;
    })
    .join('/');

  // -- Add a "/" to the start and end of the joined path
  // ---
  if (joinedPath.charAt(0) !== '/') {
    joinedPath = `/${joinedPath}`;
  }
  if (joinedPath.charAt(joinedPath.length - 1) !== '/') {
    joinedPath += '/';
  }
  return joinedPath;
};

export const getPathName = (url) => {
  const path = url.replace(settings.BASE_URL(), '');
  return pathJoin(['/', path]);
};

export const getApiPathName = (url) => {
  const path = url.replace(settings.BASE_API_DOMAIN(), '');
  return pathJoin(['/', path]);
};

// = =======================================
// GENERAL HELPERS
// = =======================================

export const toggleListSelection = (list, key) => {
  const selectedKeys = [...list];
  const keyIndex = selectedKeys.indexOf(key);

  if (keyIndex >= 0) {
    selectedKeys.splice(keyIndex, 1);
  }
  else {
    selectedKeys.push(key);
  }
  return selectedKeys;
};
